import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { ASSETS } from "../../lib/assets";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    ["services", t.nav.services],
    ["equipment", t.nav.equipment],
    ["certifications", t.nav.certifications],
    ["why", t.nav.why],
    ["waterproofing", t.nav.waterproofing],
  ];

  const go = (id) => { setOpen(false); scrollTo(id); };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass-nav border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("hero")} className="flex items-center gap-3 group" data-testid="logo-home">
          <img src={ASSETS.brandSymbol} alt="Metal Covering" className="h-9 w-9 object-contain" />
          <span className="font-display text-lg font-semibold tracking-wide text-white leading-none">
            METAL <span className="text-brand-azure">COVERING</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map(([id, label]) => (
            <button
              key={id}
              onClick={() => go(id)}
              className="link-underline font-condensed text-sm uppercase tracking-[0.15em] text-slate-300 hover:text-white transition-colors"
              data-testid={`nav-${id}`}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            className="font-condensed text-sm font-semibold tracking-wider text-slate-300 hover:text-white transition-colors border border-white/15 rounded-full px-3 py-1"
            data-testid="lang-toggle"
            aria-label="Toggle language"
          >
            {lang === "it" ? "IT / EN" : "EN / IT"}
          </button>
          <button
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center font-condensed text-sm uppercase tracking-[0.15em] font-semibold text-brand-navy bg-brand-azure hover:bg-white transition-colors rounded-full px-5 py-2.5"
            data-testid="nav-quote"
          >
            {t.nav.quote}
          </button>
          <button
            className="lg:hidden text-white p-1"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden glass-nav border-t border-white/5"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map(([id, label]) => (
                <button key={id} onClick={() => go(id)} className="text-left font-condensed text-lg uppercase tracking-wider text-slate-200" data-testid={`mnav-${id}`}>
                  {label}
                </button>
              ))}
              <button onClick={() => go("contact")} className="mt-2 text-center font-condensed uppercase tracking-wider font-semibold text-brand-navy bg-brand-azure rounded-full px-5 py-3" data-testid="mnav-quote">
                {t.nav.quote}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
